// Main editor page. Coordinates the top bar, block library, preview pane,
// and edit/settings panel — all wired through `useDraft` for autosave.

import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDraft } from '../hooks/useDraft';
import { useUpcomingEvents } from '../hooks/useUpcomingEvents';
import { renderFullHtml } from './render-html';
import { makeBlock, uid } from './blocks';
import EditorTopBar from './EditorTopBar';
import BlockLibrary from './BlockLibrary';
import PreviewPane from './PreviewPane';
import { EditPanel, SettingsPanel } from './EditPanel';
import ExportModal from './ExportModal';
import RecentCoverageModal from './RecentCoverageModal';
import SendPanel from '../newsletter/SendPanel';
import { UpcomingEventsContext } from './UpcomingEventsContext';
import type { BlockType } from '@shared/types';

export default function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const {
    draft,
    loading,
    loadErr,
    saveStatus,
    updateName,
    updateSubject,
    updatePreheader,
    setBlocks,
    reload,
  } = useDraft(id);

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [showExport, setShowExport] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);

  const { events: upcomingEvents } = useUpcomingEvents();

  const selectedBlock = draft?.blocks.find((b) => b.id === selectedBlockId);

  const exportedHtml = useMemo(
    () =>
      draft
        ? renderFullHtml(
            { blocks: draft.blocks, preheader: draft.preheader },
            { dbEvents: upcomingEvents },
          )
        : '',
    [draft, upcomingEvents],
  );

  if (loading) {
    return <div className="h-full flex items-center justify-center text-slate-400">Loading draft…</div>;
  }
  if (loadErr || !draft) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 text-sm gap-2">
        <p>Could not load this draft.</p>
        {loadErr && <p className="text-red-600 text-xs">{loadErr}</p>}
      </div>
    );
  }

  const addBlock = (type: BlockType) => {
    const newBlock = makeBlock(type);
    setBlocks([...draft.blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
  };

  const addBlockAtIndex = (type: BlockType, index: number) => {
    const newBlock = makeBlock(type);
    const next = [...draft.blocks];
    next.splice(index, 0, newBlock);
    setBlocks(next);
    setSelectedBlockId(newBlock.id);
  };

  const deleteBlock = (blockId: string) => {
    setBlocks(draft.blocks.filter((b) => b.id !== blockId));
    if (selectedBlockId === blockId) setSelectedBlockId(null);
  };

  const duplicateBlock = (blockId: string) => {
    const idx = draft.blocks.findIndex((b) => b.id === blockId);
    if (idx === -1) return;
    const orig = draft.blocks[idx];
    const copy = { id: uid(), type: orig.type, data: JSON.parse(JSON.stringify(orig.data)) };
    const next = [...draft.blocks];
    next.splice(idx + 1, 0, copy);
    setBlocks(next);
  };

  const reorderBlocks = (fromIdx: number, toIdx: number) => {
    const next = [...draft.blocks];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setBlocks(next);
  };

  const updateBlockData = (blockId: string, newData: Record<string, unknown>) => {
    setBlocks(draft.blocks.map((b) => (b.id === blockId ? { ...b, data: newData } : b)));
  };

  return (
    <UpcomingEventsContext.Provider value={upcomingEvents}>
    <div className="h-full flex flex-col bg-slate-100 font-body" style={{ fontFamily: "'Source Sans 3', -apple-system, sans-serif" }}>
      <EditorTopBar
        draft={draft}
        onRename={updateName}
        saveStatus={saveStatus}
        onExport={() => setShowExport(true)}
        onSend={() => setShowSend(true)}
        previewMode={previewMode}
        setPreviewMode={setPreviewMode}
        onSettings={() => { setShowSettings((s) => !s); setSelectedBlockId(null); }}
        showSettings={showSettings}
        onCoverage={() => setShowCoverage(true)}
      />
      {saveStatus === 'conflict' && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800 flex items-center gap-3">
          <span>This draft was edited elsewhere. Your latest changes weren't saved.</span>
          <button onClick={reload} className="ml-auto btn-secondary text-xs">Reload server copy</button>
        </div>
      )}
      <div className="flex-1 flex overflow-hidden">
        <BlockLibrary onAdd={addBlock} />
        <PreviewPane
          blocks={draft.blocks}
          selectedId={selectedBlockId}
          onSelect={(id) => { setSelectedBlockId(id); setShowSettings(false); }}
          onReorder={reorderBlocks}
          onDelete={deleteBlock}
          onDuplicate={duplicateBlock}
          onAddAtIndex={addBlockAtIndex}
          previewMode={previewMode}
        />
        {showSettings ? (
          <SettingsPanel
            draft={draft}
            onSubjectChange={(v) => updateSubject(v)}
            onPreheaderChange={(v) => updatePreheader(v)}
            onClose={() => setShowSettings(false)}
          />
        ) : (
          <EditPanel
            block={selectedBlock}
            draftId={draft.id}
            onChange={(newData) => selectedBlockId && updateBlockData(selectedBlockId, newData)}
            onClose={() => setSelectedBlockId(null)}
            onDelete={() => selectedBlockId && deleteBlock(selectedBlockId)}
            onDuplicate={() => selectedBlockId && duplicateBlock(selectedBlockId)}
          />
        )}
      </div>
      {showExport && <ExportModal html={exportedHtml} subject={draft.subject || ''} onClose={() => setShowExport(false)} />}
      {showCoverage && <RecentCoverageModal excludeDraftId={draft.id} onClose={() => setShowCoverage(false)} />}
      {showSend && (
        <SendPanel
          draftId={draft.id}
          defaultSubject={draft.subject || draft.name}
          onClose={() => setShowSend(false)}
        />
      )}
    </div>
    </UpcomingEventsContext.Provider>
  );
}
